import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActionView } from '@/components/action-view';
import { AddNewFooter } from '@/components/add-new-footer';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  convertToMenuItemIngredientFormState,
  IngredientActions,
  MenuItemIngredientFormState,
} from './menu-item-ingredient-form';
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  ListItemSeparator,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import { MENU_INFO_ROUTES } from '@/navigators/routes';
import {
  MenuItem,
  MenuItemIngredient,
  menuItemIngredientEditable,
  useMenuInfoContext,
} from '@/contexts/menu-info-context';
import { MenuItemActions } from './menu-item-actions';
import { MenuItemIngredientActionSheet } from './menu-item-ingredient-action-sheet';
import {
  MenuItemIngredientOption,
  MenuItemIngredientPricing,
} from './menu-item-ingredient-pricing';
import { MenuItemPriceFooter } from './menu-item-price-footer';
import { MenuItemPriceHeader } from './menu-item-price-header';
import { MenuItemSearchComponent } from './search/menu-item-search-component';
import { MenuStackParams } from '@/navigators/types';
import { MICRO_CONVERSION } from '@/utils/money';
import { PackUnit } from '@/enums';
import { PageContainer } from '@/components/page-container';
import { RecyclerList } from '@/components/recycler-list';
import { SCREEN_PADDING_LARGE } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';

interface Props {
  menuItem: MenuItem;
  onDeleteMenuItem: () => void;
}
interface EditIngredientProps {
  ingredient: MenuItemIngredient;
  menuItemUUID: string;
}

export const MenuItemDetailComponent: React.FC<Props> = ({
  menuItem,
  onDeleteMenuItem,
}) => {
  const { height } = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();
  const { navigate } = useNavigation<StackNavigationProp<MenuStackParams>>();
  const { editMenuItemIngredient, removeMenuItemIngredient } =
    useMenuInfoContext();

  const [showSearchActionSheet, setShowSearchActionSheet] = useState(false);
  const [showEditIngredientActionSheet, setShowEditIngredientActionSheet] =
    useState(false);
  const [editIngredientProps, setEditIngredientProps] =
    useState<EditIngredientProps>();

  const menuItemUUID = menuItem.uuid;

  const ingredientOptions: MenuItemIngredientOption[] = useMemo(() => {
    return menuItem.ingredients.map((ingredient) => ({
      metadata: ingredient,
      title: ingredient.ingredientName,
      value: ingredient.ingredientUUID,
    }));
  }, [menuItem.ingredients]);

  const actionHandlers = (
    ingredient: MenuItemIngredient,
    menuItemId: string,
    swipeableRef: SwipeableWithInstantClose | null,
    isEditable: boolean,
  ) => {
    const handlers: {
      delete: () => void;
      edit?: () => void;
    } = {
      delete: () =>
        removeMenuItemIngredient(ingredient.ingredientMetadataUUID, menuItemId),
    };

    if (isEditable) {
      handlers.edit = () => {
        setShowEditIngredientActionSheet(true);
        setEditIngredientProps({
          ingredient,
          menuItemUUID: menuItemId,
        });
        swipeableRef!.instantClose();
      };
    }

    return handlers;
  };

  const handleFormSubmit = (formState: MenuItemIngredientFormState) => {
    editMenuItemIngredient(editIngredientProps!.ingredient.ingredientUUID, {
      base_unit:
        formState.packUnit !== PackUnit.Case ? '' : formState.basePackUnit,
      cost_in_micros: Number(formState.ingredientPrice) * MICRO_CONVERSION,
      ingredient_name: formState.ingredientName,
      pack_unit: formState.packUnit,
      unit_count:
        formState.packUnit === PackUnit.Case ? formState.baseUnitCount : 1,
    });
    setShowEditIngredientActionSheet(false);
    setEditIngredientProps(undefined);
  };

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={{
        actions: [
          {
            component: (
              <MenuItemActions
                menuItem={menuItem}
                onDelete={onDeleteMenuItem}
              />
            ),
          },
        ],
        centerTitle: isSmallLayout,
        content: isSmallLayout ? null : (
          <MenuItemPriceHeader
            menuItem={menuItem}
            onDelete={onDeleteMenuItem}
          />
        ),
        showBackButton: isSmallLayout,
        title: isSmallLayout ? menuItem.name : null,
      }}
    >
      {(scrollViewProps) => (
        <KeyboardAvoidingView
          behavior={Platform.select({
            android: undefined,
            ios: 'padding',
          })}
          style={styles.base}
        >
          <RecyclerList
            estimatedItemSize={110}
            ListHeaderComponent={
              <View style={isSmallLayout ? null : styles.headerLarge}>
                <AddNewFooter
                  onSelect={() => {
                    if (isSmallLayout) {
                      navigate(MENU_INFO_ROUTES.Search, {
                        menuItemUUID,
                      });
                    } else {
                      setShowSearchActionSheet(true);
                    }
                  }}
                  size="small"
                  title={i18n.t('add_ingredient')}
                />
                <ListItemSeparator />
              </View>
            }
            listItemProps={{
              listItemStyle: isSmallLayout
                ? styles.listItem
                : styles.listItemLarge,
              renderMainContent: (option) => (
                <MenuItemIngredientPricing
                  key={option.metadata!.ingredientMetadataUUID}
                  menuItemUUID={menuItemUUID}
                  option={option}
                />
              ),
              swipeableProps:
                Platform.OS === 'web'
                  ? undefined
                  : {
                      renderRightActions: (
                        option,
                        progress,
                        drag,
                        swipeableRef,
                      ) => {
                        const ingredient = option.metadata!;
                        const isEditable =
                          menuItemIngredientEditable(ingredient);

                        return (
                          <ActionView
                            actions={actionHandlers(
                              option.metadata!,
                              menuItemUUID,
                              swipeableRef,
                              isEditable,
                            )}
                            drag={drag}
                            progress={progress}
                          />
                        );
                      },
                    },
            }}
            listItemSeparatorProps={
              isSmallLayout
                ? undefined
                : {
                    style: styles.listItemSeparatorLarge,
                  }
            }
            sections={[
              {
                data: ingredientOptions,
                key: null,
              },
            ]}
            {...scrollViewProps}
          />
          {isSmallLayout && <MenuItemPriceFooter menuItem={menuItem} />}
          {showSearchActionSheet ? (
            <BaseActionSheet
              contentStyle={{ height: Math.floor(height * 0.75) }}
              isVisible
              modalStyle={{ height: Math.floor(height * 0.85) }}
              onClose={() => setShowSearchActionSheet(false)}
            >
              <MenuItemSearchComponent menuItem={menuItem} />
            </BaseActionSheet>
          ) : null}
          {showEditIngredientActionSheet && editIngredientProps ? (
            <MenuItemIngredientActionSheet
              action={IngredientActions.EDIT}
              ingredient={convertToMenuItemIngredientFormState(
                editIngredientProps.ingredient,
              )}
              onSubmit={handleFormSubmit}
              originalBaseUnit={editIngredientProps.ingredient.originalBaseUnit}
              setShowIngredientForm={setShowEditIngredientActionSheet}
            />
          ) : null}
        </KeyboardAvoidingView>
      )}
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
  },
  headerLarge: {
    paddingHorizontal: 8,
  },
  listItem: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  listItemLarge: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
    paddingVertical: 16,
  },
  listItemSeparatorLarge: {
    marginHorizontal: SCREEN_PADDING_LARGE,
  },
});
